import React, { useState } from "react";
import { Check, X, Sparkles, Crown, Zap, Gem } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { plans } from "../mock";
import { Button } from "./ui/button";

const planIcon = { trial: Sparkles, pro: Zap, premium: Crown, vip: Gem };

export default function Pricing({ onSelectPlan }) {
  const { t, lang, pick, L } = useLang();
  const [yearly, setYearly] = useState(true);

  const money = (v) => (lang === "es" ? `${v.toFixed(2).replace(".", ",")}$` : `$${v.toFixed(2)}`);
  const priceFor = (plan) => {
    if (plan.price === 0) return { amount: t("pricing_free_forever"), isFree: true };
    const val = yearly ? plan.price * 0.6 : plan.price;
    return {
      amount: lang === "es" ? `${val.toFixed(2).replace(".", ",")}$` : `$${val.toFixed(2)}`,
      isFree: false,
    };
  };

  return (
    <section id="pricing" className="py-20 sm:py-28 relative">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">{t("pricing_title")}</h2>
          <p className="mt-4 text-zinc-400 text-lg">{t("pricing_sub")}</p>
          <div className="mt-8 relative inline-flex items-center rounded-full bg-zinc-900 border border-white/10 p-1" data-testid="billing-toggle">
            {[[false, t("pricing_monthly")], [true, t("pricing_yearly")]].map(([v, l]) => (
              <button key={l} onClick={() => setYearly(v)} data-testid={v ? "billing-yearly" : "billing-monthly"}
                className={`relative rounded-full px-5 py-2 text-sm font-bold transition-colors ${yearly === v ? "bg-yellow-400 text-zinc-950" : "text-zinc-400 hover:text-white"}`}>{l}</button>
            ))}
            <span className="absolute -top-3 -right-6 rotate-6 rounded-full bg-emerald-400 px-2.5 py-0.5 text-[11px] font-black text-zinc-950 shadow-[0_6px_20px_-6px_rgba(52,211,153,.8)] animate-bounce [animation-duration:2.2s]" data-testid="billing-save-badge">{t("pricing_save")}</span>
          </div>
          {yearly && <p className="mt-3 text-xs text-emerald-300" data-testid="billing-yearly-note">{L("2 meses y medio gratis cada año con el plan anual", "Get 2.5 months free every year with annual billing")}</p>}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 items-start">
          {plans.map((plan) => {
            const Icon = planIcon[plan.id];
            const price = priceFor(plan);
            const hot = plan.highlight;
            return (
              <div key={plan.id} className={`relative rounded-3xl p-6 border transition-all duration-300 flex flex-col ${hot ? "border-yellow-400/60 bg-gradient-to-b from-yellow-400/[0.08] to-zinc-900 shadow-[0_20px_70px_-30px_rgba(250,204,21,0.5)] xl:-translate-y-3" : "border-white/10 bg-zinc-900/50 hover:border-white/20"}`}>
                {plan.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-zinc-950 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{pick(plan.badge)}</span>}
                <div className="flex items-center gap-2.5">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${hot ? "bg-yellow-400 text-zinc-950" : "bg-white/5 text-yellow-400"}`}><Icon className="h-5 w-5" /></div>
                  <h3 className="text-lg font-bold">{t(plan.nameKey)}</h3>
                </div>
                <p className="mt-3 text-sm text-zinc-400 min-h-[40px]">{pick(plan.tagline)}</p>
                {yearly && !price.isFree && <span className="absolute top-5 right-5 rounded-full bg-emerald-400/15 border border-emerald-400/40 px-2 py-0.5 text-[11px] font-black text-emerald-300" data-testid={`plan-save-${plan.id}`}>-40%</span>}
                <div className="mt-3 flex items-end gap-1.5">
                  {yearly && !price.isFree && <span className="text-lg font-bold text-zinc-500 line-through mb-0.5" data-testid={`plan-old-${plan.id}`}>{money(plan.price)}</span>}
                  <span className="text-3xl font-extrabold tracking-tight" data-testid={`plan-price-${plan.id}`}>{price.amount}</span>
                  {!price.isFree && <span className="text-zinc-400 mb-1 text-sm">{t("pricing_month")}</span>}
                </div>
                {!price.isFree && (yearly
                  ? <p className="mt-1 text-xs text-zinc-400" data-testid={`plan-yearly-${plan.id}`}>{L(`${money(plan.price * 0.6 * 12)} al año · ahorras ${money(plan.price * 0.4 * 12)}`, `${money(plan.price * 0.6 * 12)} per year · you save ${money(plan.price * 0.4 * 12)}`)}</p>
                  : <button onClick={() => setYearly(true)} className="mt-1 text-xs font-semibold text-emerald-300 hover:underline text-left" data-testid={`plan-switch-yearly-${plan.id}`}>{L(`Ahorra ${money(plan.price * 0.4 * 12)} al año con el plan anual →`, `Save ${money(plan.price * 0.4 * 12)}/year with annual →`)}</button>)}
                <Button onClick={() => onSelectPlan(plan, yearly)} className={`mt-5 w-full rounded-xl font-semibold py-5 ${hot ? "bg-yellow-400 hover:bg-yellow-300 text-zinc-950 shadow-[0_0_30px_-8px_rgba(250,204,21,0.7)]" : "bg-white/10 hover:bg-white/15 text-white"}`}>{pick(plan.cta)}</Button>
                <ul className="mt-6 space-y-2.5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      {f.ok ? <Check className="h-4 w-4 mt-0.5 shrink-0 text-yellow-400" /> : <X className="h-4 w-4 mt-0.5 shrink-0 text-zinc-600" />}
                      <span className={f.ok ? "text-zinc-300" : "text-zinc-500"}>{pick(f)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
